import { Link2 } from "lucide-react";
import { useState, type Dispatch, type SetStateAction } from "react";

import type { OpsDocLinkTarget } from "../../shared/operations-docs-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

export function DocsPageLinksDialog({
  linkTargets,
  selectedLinks,
  setSelectedLinks,
}: {
  linkTargets: OpsDocLinkTarget[];
  selectedLinks: string[];
  setSelectedLinks: Dispatch<SetStateAction<string[]>>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger render={<Button type="button" variant="outline" />}>
        <Link2 /> Links
        {selectedLinks.length > 0 ? (
          <Badge variant="secondary">{selectedLinks.length}</Badge>
        ) : null}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Link this page</DialogTitle>
          <DialogDescription>
            Linked pages appear on the matching customer or tool. Links do not change who can read
            this page.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-1">
          {linkTargets.length === 0 ? (
            <p className="rounded-sm border border-dashed p-4 text-sm text-muted-foreground">
              No customers or tools are available to link.
            </p>
          ) : (
            linkTargets.map((target) => {
              const key = `${target.entityType}:${target.entityId}`;
              return (
                <label
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-3 py-2 hover:bg-muted"
                  key={key}
                >
                  <input
                    checked={selectedLinks.includes(key)}
                    className="size-4 accent-primary"
                    onChange={(event) => {
                      setSelectedLinks((current) =>
                        event.target.checked
                          ? [...current, key]
                          : current.filter((value) => value !== key),
                      );
                    }}
                    type="checkbox"
                  />
                  <span className="min-w-0 text-sm">
                    <span className="block truncate font-semibold">{target.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {target.customerName} · {target.entityType}
                    </span>
                  </span>
                </label>
              );
            })
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => {
              setOpen(false);
            }}
            type="button"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
